import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../utils/apiConfig';

/**
 * Custom hook to fetch trade size distribution data
 * Used for Big vs Small traders panel
 * @param {number} hours - Hours to look back (default 24)
 * @param {number} refreshInterval - Refresh interval in ms (default 10s)
 */
export function useTradeSizeDistribution(hours = 24, refreshInterval = 10000, { enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalTrades, setTotalTrades] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/trade-size-distribution?hours=${hours}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch distribution: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch distribution');
      }

      // Extract range data from response (API returns ranges at root level)
      const { success, totalTrades, lastUpdate, ...rangeData } = result;

      // Calculate total trades from all ranges if not provided
      const total = totalTrades || Object.values(rangeData).reduce((sum, r) => sum + (r.count || 0), 0);

      setData(rangeData);
      setTotalTrades(total);
      setLastUpdate(lastUpdate || new Date().toISOString());
      setError(null);
    } catch (err) {
      console.error('[Distribution] Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => {
    if (!enabled) return;

    // Fetch immediately
    fetchData();

    // Set up refresh interval
    const interval = setInterval(fetchData, refreshInterval);

    return () => clearInterval(interval);
  }, [fetchData, refreshInterval, enabled]);

  // Helper to format range display
  const formatRange = (range) => {
    const labels = {
      '10K-100K': '$10K - $100K',
      '100K-500K': '$100K - $500K',
      '500K-1M': '$500K - $1M',
      '1M-10M': '$1M - $10M',
      '10M+': '$10M+',
    };
    return labels[range] || range;
  };

  return {
    data,
    loading,
    error,
    totalTrades,
    lastUpdate,
    refresh: fetchData,
    formatRange,
  };
}

export default useTradeSizeDistribution;
